import { useState, useEffect } from 'react';
import { adminAPI, APIError } from '../lib/api';
import type { AdminStats } from '../types/api';
import { StatCard, TierBreakdown, MRRBreakdown } from '../components/StatCard';
import styles from './Dashboard.module.css';

export function Dashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchStats() {
      try {
        const data = await adminAPI.getStats();
        setStats(data);
      } catch (err) {
        if (err instanceof APIError) {
          setError(err.message);
        } else {
          setError('Failed to load stats');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, []);

  if (loading) {
    return <div className={styles.loading}>Loading stats...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!stats) {
    return null;
  }

  const totalMRR = stats.mrr.active + stats.mrr.at_risk + stats.mrr.trial_pipeline;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Dashboard</h1>
      </header>

      <div className={styles.stats}>
        <StatCard label="Total Organizations" value={stats.total_organizations}>
          <TierBreakdown
            free={stats.by_tier.free}
            pro={stats.by_tier.pro}
            enterprise={stats.by_tier.enterprise}
          />
        </StatCard>

        <StatCard label="Monthly Recurring Revenue" value={`$${totalMRR.toLocaleString()}`}>
          <MRRBreakdown
            active={stats.mrr.active}
            atRisk={stats.mrr.at_risk}
            trialPipeline={stats.mrr.trial_pipeline}
          />
        </StatCard>
      </div>
    </div>
  );
}
